import { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  User,
  CreditCard,
  Settings,
  Calendar,
  Activity,
  Key,
  Bell,
  MessageCircle,
  Phone,
  Send,
  PlayCircle,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { CreditBalanceCard } from '@/components/CreditBalanceCard';
import { TrainingVideoModal } from '@/components/TrainingVideoModal';
import { useLocationTrainingVideo } from '@/hooks/useLocationTrainingVideo';
import { supabase } from '@/integrations/supabase/client';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { PhoneNumberSelector } from '@/components/PhoneNumberSelector';

interface UserProfile {
  email: string;
  license_type?: string;
  trial_expires_at?: string;
  last_sign_in_at?: string;
  subscription_status?: string;
  license_id?: string;
}

interface License {
  id: string;
  name: string;
  description: string | null;
  features: any;
  max_calls_per_month: number | null;
  max_campaigns: number | null;
}

interface NotificationPreferences {
  tier1_enabled: boolean;
  tier2_enabled: boolean;
  tier2_morning_time: string;
  tier2_afternoon_time: string;
  digest_format: string;
  sms_enabled: boolean;
  sms_phone_number?: string;
  sms_notification_phone?: string;
  sms_test_destination?: string;
}

// Phase 1: SMS Feature Flag - SMS delivery enabled
const SMS_FEATURE_ENABLED = true;

export default function Profile() {
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [license, setLicense] = useState<License | null>(null);
  const [notificationPrefs, setNotificationPrefs] =
    useState<NotificationPreferences>({
      tier1_enabled: true,
      tier2_enabled: true,
      tier2_morning_time: '09:00:00',
      tier2_afternoon_time: '15:00:00',
      digest_format: 'html',
      sms_enabled: false,
      sms_phone_number: undefined,
      sms_notification_phone: undefined,
      sms_test_destination: undefined,
    });
  const [loading, setLoading] = useState(true);
  const { hasVideo, displayTitle } = useLocationTrainingVideo('profile');
  const [isTestingSMS, setIsTestingSMS] = useState(false);

  // Local state for SMS test destination to avoid immediate DB updates
  const [localSmsTestDestination, setLocalSmsTestDestination] = useState('');

  const { user, apiKeys } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Get active tab from URL params
  const searchParams = new URLSearchParams(location.search);
  const activeTab = searchParams.get('tab') || 'overview';

  useEffect(() => {
    fetchProfile();
    fetchLicense();
    fetchNotificationPreferences();
  }, []);

  // Sync local state with notification preferences
  useEffect(() => {
    setLocalSmsTestDestination(notificationPrefs.sms_test_destination || '');
  }, [notificationPrefs.sms_test_destination]);

  const fetchLicense = async () => {
    if (!user) return;

    try {
      const { data: profileData } = await supabase
        .from('profiles')
        .select('license_id')
        .eq('user_id', user.id)
        .single();

      if (profileData?.license_id) {
        const { data: licenseData } = await supabase
          .from('licenses')
          .select('*')
          .eq('id', profileData.license_id)
          .single();

        if (licenseData) {
          setLicense(licenseData);
        }
      }
    } catch {
      // license data unavailable, skip
    }
  };

  const fetchProfile = async () => {
    if (!user) return;

    try {
      // Get basic user info
      const userProfile: UserProfile = {
        email: user.email || '',
        last_sign_in_at: user.last_sign_in_at || undefined,
      };

      // Try to get additional profile data (gracefully handle missing columns)
      try {
        const { data: profileData } = await supabase
          .from('profiles')
          .select('*')
          .eq('user_id', user.id)
          .single();

        if (profileData) {
          // Only set properties if they exist in the response
          if (
            'license_type' in profileData &&
            typeof profileData.license_type === 'string'
          ) {
            userProfile.license_type = profileData.license_type;
          }
          if (
            'trial_expires_at' in profileData &&
            typeof profileData.trial_expires_at === 'string'
          ) {
            userProfile.trial_expires_at = profileData.trial_expires_at;
          }
          if (
            'license_id' in profileData &&
            typeof profileData.license_id === 'string'
          ) {
            userProfile.license_id = profileData.license_id;
          }

          // Fetch license details if license_id exists
          if (profileData.license_id) {
            try {
              const { data: licenseData } = await supabase
                .from('licenses')
                .select('*')
                .eq('id', profileData.license_id)
                .single();

              if (licenseData) {
                setLicense(licenseData);
              }
            } catch {
              // license data unavailable, skip
            }
          }
        }
      } catch {
        // profile fetch errors are non-fatal
      }

      setProfile(userProfile);
    } catch (error) {
      console.error('Error fetching profile:', error);
      // Set basic profile even on error
      setProfile({
        email: user?.email || '',
        last_sign_in_at: user?.last_sign_in_at || undefined,
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchNotificationPreferences = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('user_notification_preferences')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') {
        // PGRST116 = no rows returned
        throw error;
      }

      if (data) {
        setNotificationPrefs(data);
      }
    } catch (error) {
      console.error('Error fetching notification preferences:', error);
    }
  };

  const updateNotificationPreferences = async (
    updates: Partial<NotificationPreferences>,
    options: { showToast?: boolean } = { showToast: true }
  ) => {
    if (!user) return;

    try {
      const newPrefs = { ...notificationPrefs, ...updates };

      const { error } = await supabase
        .from('user_notification_preferences')
        .upsert({
          user_id: user.id,
          ...newPrefs,
        });

      if (error) throw error;

      setNotificationPrefs(newPrefs);

      if (options.showToast) {
        toast.success('Notification preferences updated');
      }
    } catch (error) {
      console.error('Error updating notification preferences:', error);
      toast.error('Failed to update notification preferences');
    }
  };

  const handleTestSMS = async () => {
    if (!SMS_FEATURE_ENABLED) {
      toast.info('SMS notifications coming in Phase 2! 📱');
      return;
    }

    if (!notificationPrefs.sms_phone_number) {
      toast.error('Please select a sending phone number for SMS testing');
      return;
    }

    if (!localSmsTestDestination) {
      toast.error('Please enter a destination phone number for SMS testing');
      return;
    }

    setIsTestingSMS(true);

    try {
      const { data, error } = await supabase.functions.invoke('test-sms', {
        body: {
          toNumber: localSmsTestDestination,
          message:
            'This is a test SMS from your AI calling system. If you receive this, SMS delivery is working correctly!',
        },
      });

      if (error) throw error;

      if (data.success) {
        toast.success(
          `Test SMS sent successfully from ${data.from} to ${data.to}`
        );
      } else {
        throw new Error(data.error || 'Unknown error');
      }
    } catch (error) {
      console.error('SMS test error:', error);
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to send test SMS';

      if (errorMessage.includes('Twilio credentials')) {
        toast.error(
          'Twilio API keys not configured. Please set up your Twilio credentials in API Settings.'
        );
      } else if (errorMessage.includes('No active phone numbers')) {
        toast.error(
          'No active phone numbers found. Please purchase a phone number first.'
        );
      } else if (errorMessage.includes('Twilio API Error')) {
        // Display Twilio errors directly to user
        toast.error(
          `SMS Failed: ${errorMessage.replace(
            'Twilio API Error',
            'Twilio Error'
          )}`
        );
      } else {
        toast.error(errorMessage);
      }
    } finally {
      setIsTestingSMS(false);
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  const formatDateTime = (dateString?: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  const handleTabChange = (value: string) => {
    const newUrl = value === 'overview' ? '/profile' : `/profile?tab=${value}`;
    navigate(newUrl, { replace: true });
  };

  if (loading) {
    return (
      <div className='container mx-auto p-6 space-y-6'>
        <div className='max-w-4xl mx-auto'>
          <div className='animate-pulse'>
            <div className='h-8 bg-muted rounded w-48 mb-2'></div>
            <div className='h-4 bg-muted rounded w-96 mb-8'></div>
            <div className='h-64 bg-muted rounded'></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className='container mx-auto p-6 space-y-6'>
      
        {/* Header */}
        <div className='mb-8'>
          <h1 className='text-3xl font-bold text-foreground'>Profile</h1>
          <p className='text-muted-foreground'>
            Manage your account settings and information
          </p>
        </div>

        {hasVideo && (
          <div className='mb-6'>
            <TrainingVideoModal
              trigger={
                <Button variant='outline' size='sm'>
                  <PlayCircle className='h-4 w-4 mr-2' />
                  {displayTitle || 'View Training'}
                </Button>
              }
              locationKey='profile'
            />
          </div>
        )}

        {/* Profile Tabs */}
        <Tabs value={activeTab} onValueChange={handleTabChange}>
          <TabsList className='mb-6'>
            <TabsTrigger value='overview' className='flex items-center gap-2'>
              <User className='h-4 w-4' />
              Overview
            </TabsTrigger>
            <TabsTrigger value='credits' className='flex items-center gap-2'>
              <CreditCard className='h-4 w-4' />
              Credits
            </TabsTrigger>
            <TabsTrigger
              value='notifications'
              className='flex items-center gap-2'
            >
              <Bell className='h-4 w-4' />
              Notifications
            </TabsTrigger>
          </TabsList>

          <TabsContent value='overview' className='space-y-6'>
            {/* User Information Card */}
            <Card>
              <CardHeader>
                <CardTitle className='flex items-center gap-2'>
                  <User className='h-5 w-5' />
                  Account Information
                </CardTitle>
                <CardDescription>
                  Your basic account details and subscription information
                </CardDescription>
              </CardHeader>
              <CardContent className='space-y-4'>
                <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                  <div>
                    <label className='text-sm font-medium text-muted-foreground'>
                      Email
                    </label>
                    <div className='text-sm font-mono bg-muted/50 p-2 rounded mt-1'>
                      {profile?.email || 'N/A'}
                    </div>
                  </div>

                  <div>
                    <label className='text-sm font-medium text-muted-foreground'>
                      License
                    </label>
                    <div className='mt-1'>
                      {license ? (
                        <div className='space-y-1'>
                          <Badge variant='secondary'>{license.name}</Badge>
                          <div className='text-xs text-muted-foreground'>
                            {license.max_calls_per_month
                              ? `${license.max_calls_per_month} monthly credits`
                              : 'Unlimited credits'}
                            {license.max_campaigns &&
                              ` • ${license.max_campaigns} campaigns max`}
                          </div>
                        </div>
                      ) : (
                        <span className='text-sm text-muted-foreground'>
                          No license assigned
                        </span>
                      )}
                    </div>
                  </div>

                  <div>
                    <label className='text-sm font-medium text-muted-foreground'>
                      Trial Expiration
                    </label>
                    <div className='text-sm mt-1'>
                      {profile?.trial_expires_at ? (
                        <span className='flex items-center gap-2'>
                          <Calendar className='h-4 w-4' />
                          {formatDate(profile.trial_expires_at)}
                        </span>
                      ) : (
                        <span className='text-muted-foreground'>
                          No trial active
                        </span>
                      )}
                    </div>
                  </div>

                  <div>
                    <label className='text-sm font-medium text-muted-foreground'>
                      Last Login
                    </label>
                    <div className='text-sm mt-1'>
                      <span className='flex items-center gap-2'>
                        <Activity className='h-4 w-4' />
                        {formatDateTime(profile?.last_sign_in_at)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* API Management */}
                <div className='border-t pt-4 mt-6'>
                  <div className='flex items-center justify-between'>
                    <div>
                      <h3 className='text-sm font-medium'>API Configuration</h3>
                      <p className='text-sm text-muted-foreground mt-1'>
                        Manage your API keys and integrations
                      </p>
                    </div>
                    <Button
                      variant='outline'
                      size='sm'
                      onClick={() => navigate('/api-settings')}
                      className='flex items-center gap-2'
                    >
                      <Key className='h-4 w-4' />
                      Manage API Keys
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* License Features Card */}
            {license && license.features && (
              <Card>
                <CardHeader>
                  <CardTitle className='flex items-center gap-2'>
                    <Settings className='h-5 w-5' />
                    License Features
                  </CardTitle>
                  <CardDescription>
                    Features included in your {license.name} license
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                    {license.features.white_label ? (
                      <div className='flex items-center gap-2 text-sm'>
                        <div className='h-2 w-2 rounded-full bg-success'></div>
                        White-label domain support
                      </div>
                    ) : (
                      <div className='flex items-center gap-2 text-sm text-muted-foreground'>
                        <div className='h-2 w-2 rounded-full bg-muted'></div>
                        White-label domain (upgrade required)
                      </div>
                    )}

                    <div className='flex items-center gap-2 text-sm'>
                      <div className='h-2 w-2 rounded-full bg-success'></div>
                      {license.features.clients_limit || 'Unlimited'} client
                      accounts
                    </div>

                    <div className='flex items-center gap-2 text-sm'>
                      <div className='h-2 w-2 rounded-full bg-success'></div>
                      {license.max_campaigns || 'Unlimited'} campaigns
                    </div>

                    <div className='flex items-center gap-2 text-sm'>
                      <div className='h-2 w-2 rounded-full bg-success'></div>
                      {license.max_calls_per_month || 'Unlimited'} monthly
                      credits
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Usage Summary Card */}
            <Card>
              <CardHeader>
                <CardTitle className='flex items-center gap-2'>
                  <Activity className='h-5 w-5' />
                  Usage Summary
                </CardTitle>
                <CardDescription>
                  Current month's activity and usage statistics
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
                  <div className='text-center space-y-2'>
                    <div className='text-2xl font-bold text-muted-foreground'>
                      --
                    </div>
                    <div className='text-sm text-muted-foreground'>
                      API Calls This Month
                    </div>
                  </div>
                  <div className='text-center space-y-2'>
                    <div className='text-2xl font-bold text-muted-foreground'>
                      --
                    </div>
                    <div className='text-sm text-muted-foreground'>
                      Active Campaigns
                    </div>
                  </div>
                  <div className='text-center space-y-2'>
                    <div className='text-2xl font-bold text-muted-foreground'>
                      --
                    </div>
                    <div className='text-sm text-muted-foreground'>
                      Total Prospects
                    </div>
                  </div>
                </div>
                <div className='text-center mt-4'>
                  <p className='text-sm text-muted-foreground'>
                    Detailed analytics coming soon
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value='credits'>
            <CreditBalanceCard />
          </TabsContent>

          <TabsContent value='notifications' className='space-y-6'>
            <Card>
              <CardHeader>
                <CardTitle className='flex items-center gap-2'>
                  <Bell className='h-5 w-5' />
                  Notification Preferences
                </CardTitle>
                <CardDescription>
                  Configure how and when you receive notifications from your
                  Action Center
                </CardDescription>
              </CardHeader>
              <CardContent className='space-y-6'>
                {/* Immediate Notifications */}
                <div className='space-y-4'>
                  <div className='flex items-center justify-between'>
                    <div>
                      <h3 className='text-sm font-medium'>
                        Immediate Notifications
                      </h3>
                      <p className='text-sm text-muted-foreground'>
                        Real-time alerts for inbound calls, appointments, and
                        urgent actions
                      </p>
                    </div>
                    <Switch
                      checked={notificationPrefs.tier1_enabled}
                      onCheckedChange={(checked) =>
                        updateNotificationPreferences({
                          tier1_enabled: checked,
                        })
                      }
                    />
                  </div>
                </div>

                {/* Email Format */}
                <div className='space-y-2 border-t pt-4'>
                  <Label>Email Format</Label>
                  <Select
                    value={notificationPrefs.digest_format}
                    onValueChange={(value) =>
                      updateNotificationPreferences({ digest_format: value })
                    }
                  >
                    <SelectTrigger className='w-48'>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value='html'>Rich HTML</SelectItem>
                      <SelectItem value='text'>Plain Text</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>

            {/* SMS Notifications Card */}
            <Card>
              <CardHeader>
                <CardTitle className='flex items-center gap-2'>
                  <MessageCircle className='h-5 w-5' />
                  SMS Notifications
                  {!SMS_FEATURE_ENABLED && (
                    <Badge variant='outline' className='text-xs'>
                      Phase 2
                    </Badge>
                  )}
                </CardTitle>
                <CardDescription>
                  Get action alerts via SMS in addition to email notifications
                </CardDescription>
              </CardHeader>
              <CardContent className='space-y-6'>
                {/* A2P Registration Notice */}
                <div className='bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4'>
                  <div className='flex items-start gap-3'>
                    <MessageCircle className='h-5 w-5 text-amber-600 dark:text-amber-400 mt-0.5' />
                    <div className='space-y-2'>
                      <h4 className='text-sm font-medium text-amber-800 dark:text-amber-200'>
                        A2P Registration Required
                      </h4>
                      <p className='text-sm text-amber-700 dark:text-amber-300'>
                        To enable text messaging alerts, you must submit A2P
                        registration with Twilio. This is a one-time process
                        required for business messaging compliance.
                      </p>
                      <a
                        href='https://help.twilio.com/articles/11847054539547-A2P-10DLC-Campaign-Approval-Requirements'
                        target='_blank'
                        rel='noopener noreferrer'
                        className='inline-flex items-center gap-1 text-sm text-amber-700 dark:text-amber-300 hover:text-amber-900 dark:hover:text-amber-100 underline'
                      >
                        View A2P Registration Documentation
                        <svg
                          className='h-3 w-3'
                          fill='none'
                          viewBox='0 0 24 24'
                          strokeWidth={1.5}
                          stroke='currentColor'
                        >
                          <path
                            strokeLinecap='round'
                            strokeLinejoin='round'
                            d='M13.5 6H5.25A2.25 2.25 0 0 0 3 8.25v10.5A2.25 2.25 0 0 0 5.25 21h10.5A2.25 2.25 0 0 0 18 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25'
                          />
                        </svg>
                      </a>
                    </div>
                  </div>
                </div>
                {/* Twilio Setup Check */}
                {SMS_FEATURE_ENABLED &&
                  (!apiKeys?.twilio ||
                    !apiKeys?.twilioSid ||
                    !apiKeys?.twilioAuthToken) && (
                    <div className='bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4'>
                      <div className='flex items-start gap-3'>
                        <Settings className='h-5 w-5 text-blue-600 dark:text-blue-400 mt-0.5' />
                        <div className='space-y-2'>
                          <h4 className='text-sm font-medium text-blue-800 dark:text-blue-200'>
                            Twilio Setup Required
                          </h4>
                          <p className='text-sm text-blue-700 dark:text-blue-300'>
                            SMS notifications require Twilio API credentials.
                            Please configure your Twilio settings first.
                          </p>
                          <Button
                            variant='outline'
                            size='sm'
                            onClick={() => navigate('/api-settings')}
                            className='text-blue-700 dark:text-blue-300 border-blue-300 dark:border-blue-700 hover:bg-blue-100 dark:hover:bg-blue-900/20'
                          >
                            Configure Twilio Settings
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}

                {/* SMS Action Alerts */}
                <div className='space-y-4'>
                  <div className='flex items-center justify-between'>
                    <div>
                      <h3 className='text-sm font-medium'>SMS Action Alerts</h3>
                      <p className='text-sm text-muted-foreground'>
                        Real-time SMS notifications for urgent actions and
                        appointments
                      </p>
                    </div>
                    <Switch
                      checked={notificationPrefs.sms_enabled}
                      onCheckedChange={(checked) => {
                        if (!SMS_FEATURE_ENABLED) return;
                        if (
                          !apiKeys?.twilio ||
                          !apiKeys?.twilioSid ||
                          !apiKeys?.twilioAuthToken
                        ) {
                          toast.error(
                            'Please configure your Twilio API settings first'
                          );
                          return;
                        }
                        updateNotificationPreferences({ sms_enabled: checked });
                      }}
                      disabled={
                        !SMS_FEATURE_ENABLED ||
                        !apiKeys?.twilio ||
                        !apiKeys?.twilioSid ||
                        !apiKeys?.twilioAuthToken
                      }
                    />
                  </div>

                  {notificationPrefs.sms_enabled && (
                    <div className='ml-4 space-y-4'>
                      {/* Phone Number Selection */}
                      <div className='space-y-2'>
                        <Label className='flex items-center gap-2'>
                          <Phone className='h-4 w-4' />
                          SMS Phone Number (Sender)
                        </Label>
                        <div className='w-full max-w-md'>
                          <PhoneNumberSelector
                            value={notificationPrefs.sms_phone_number || ''}
                            onChange={(phoneNumber) =>
                              updateNotificationPreferences({
                                sms_phone_number: phoneNumber,
                              })
                            }
                            disabled={!SMS_FEATURE_ENABLED}
                            mode='sms-only'
                          />
                        </div>
                        <p className='text-xs text-muted-foreground'>
                          Select the phone number to send SMS notifications from
                        </p>
                      </div>

                      {/* Your Mobile Number for Notifications */}
                      <div className='space-y-2'>
                        <Label className='flex items-center gap-2'>
                          <MessageCircle className='h-4 w-4' />
                          Your Mobile Number (Notifications)
                        </Label>
                        <div className='w-full max-w-md'>
                          <Input
                            placeholder='Enter your mobile number (e.g., +1234567890)'
                            value={
                              notificationPrefs.sms_notification_phone || ''
                            }
                            onChange={(e) =>
                              updateNotificationPreferences({
                                sms_notification_phone: e.target.value,
                              })
                            }
                            disabled={!SMS_FEATURE_ENABLED}
                          />
                        </div>
                        <p className='text-xs text-muted-foreground'>
                          Your personal mobile number to receive real SMS
                          notifications
                        </p>
                      </div>

                      {/* SMS Test Destination */}
                      <div className='space-y-2'>
                        <Label className='flex items-center gap-2'>
                          <Send className='h-4 w-4' />
                          SMS Test Destination
                        </Label>
                        <div className='w-full max-w-md'>
                          <Input
                            placeholder='Enter test number (e.g., +1234567890)'
                            value={localSmsTestDestination}
                            onChange={(e) =>
                              setLocalSmsTestDestination(e.target.value)
                            }
                            onBlur={() => {
                              if (
                                localSmsTestDestination !==
                                notificationPrefs.sms_test_destination
                              ) {
                                updateNotificationPreferences(
                                  {
                                    sms_test_destination:
                                      localSmsTestDestination,
                                  },
                                  { showToast: false }
                                );
                              }
                            }}
                            onKeyDown={(e) => {
                              if (
                                e.key === 'Enter' &&
                                localSmsTestDestination !==
                                  notificationPrefs.sms_test_destination
                              ) {
                                updateNotificationPreferences(
                                  {
                                    sms_test_destination:
                                      localSmsTestDestination,
                                  },
                                  { showToast: false }
                                );
                                e.currentTarget.blur();
                              }
                            }}
                            disabled={!SMS_FEATURE_ENABLED}
                          />
                        </div>
                        <p className='text-xs text-muted-foreground'>
                          Number to use for SMS testing (can be different from
                          your notification number)
                        </p>
                      </div>

                      {/* Test SMS */}
                      <div className='space-y-2'>
                        <Button
                          variant='outline'
                          size='sm'
                          onClick={handleTestSMS}
                          disabled={
                            !notificationPrefs.sms_phone_number ||
                            !localSmsTestDestination ||
                            !SMS_FEATURE_ENABLED ||
                            isTestingSMS
                          }
                          className='flex items-center gap-2'
                        >
                          <Send className='h-4 w-4' />
                          {isTestingSMS
                            ? 'Sending...'
                            : SMS_FEATURE_ENABLED
                            ? 'Send Test SMS'
                            : 'Coming in Phase 2'}
                        </Button>
                        <p className='text-xs text-muted-foreground'>
                          {SMS_FEATURE_ENABLED
                            ? 'Send a test message to verify SMS delivery to your mobile'
                            : 'SMS delivery will be available in Phase 2 implementation'}
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                {!SMS_FEATURE_ENABLED && (
                  <div className='bg-muted/50 p-4 rounded-lg'>
                    <div className='flex items-center gap-2 mb-2'>
                      <MessageCircle className='h-4 w-4 text-muted-foreground' />
                      <span className='text-sm font-medium'>
                        SMS Feature Preview
                      </span>
                    </div>
                    <p className='text-sm text-muted-foreground'>
                      SMS notifications are being prepared for Phase 2. This
                      will allow you to receive action alerts and urgent
                      notifications directly to your phone via SMS, using your
                      existing Twilio integration and phone number management.
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    
  );
}
